import Select from 'react-select'
import { useState, useRef, useMemo, useEffect } from 'react'
import { Icon } from '@iconify/react'
import { KTSVG, toAbsoluteUrl } from '../../../../_metronic/helpers'
import { isSuperAdmin } from '../../store/userRoleGrants'
import AddAccountPopup from './AddAccountPopup'
import {
  useGetExistingCompaniesOfInvestorQuery,
  GetExistingCompaniesOfInvestorQuery,
  useGetAllUsersOfAccountQuery,
  useGetAllFundsOfUserQuery,
  useRemoveUserFromAccountMutation,
  SyncCompanyInput,
} from '../../../../generated/graphql'
import { graphqlRequestClient, queryClient } from '../../../../queries/client'
import { DeleteConfirmPopup } from '../../../../utils/DeleteConfirmPopup'
import { selectCustomStyles } from '../../../../utils/Select'
import { getUserId } from '../../../../utils/SessionStorageUtils'
import FundTable from './Investor-FundAgTable'
import RemoveCompanies from './RemoveUserCompanies'
import UpdateUserRolePopup from './UpdateUser'
import AddFundPopup from './AddFundPopup'
import Toast, { ToastType } from '../../../../utils/toast'
import SyncInvestorPopup from './SyncInvestorPopup'
import { AddUserPopup } from './AddUserPopup'
import { roles } from '../utils'

interface RemoveUsersProps {
  accountId: string
}

export interface UserOption {
  label: string
  value: string
  role: string | null
  name: string
  email: string
  accountId?: string
  isAccountAdmin?: boolean
}

interface UserAction {
  id: string
  label: string
  icon: string
  onClick: () => void
  visible: boolean
}

const RemoveUsers: React.FC<RemoveUsersProps> = ({ accountId }) => {
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined)
  const [selectedUserName, setSelectedUserName] = useState<string | undefined>(undefined)
  const [selectedUserRole, setSelectedUserRole] = useState<string | null>(null)
  const [selectedUserAccountId, setSelectedUserAccountId] = useState<string | null>(null)
  const [selectedUserIsAccountAdmin, setSelectedUserIsAccountAdmin] = useState<boolean>(false)
  const [selectedFundId, setSelectedFundId] = useState<number | null>(null)
  const [isAddDropdownOpen, setIsAddDropdownOpen] = useState(false)
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false)
  const [isDeleteLoading, setIsDeleteLoading] = useState(false)
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [showUpdateRole, setShowUpdateRole] = useState(false)
  const [showAddFund, setShowAddFund] = useState(false)
  const [showSyncInvestor, setShowSyncInvestor] = useState(false)
  const [showAddUser, setShowAddUser] = useState(false)
  const [showAddAccount, setShowAddAccount] = useState(false)
  const superAdmin = isSuperAdmin()
  const [activeTab, setActiveTab] = useState<'funds' | 'companies'>('funds')
  const [hoveredOption, setHoveredOption] = useState<string | null>(null)

  const addDropdownRef = useRef<HTMLDivElement>(null)
  const userDropdownRef = useRef<HTMLDivElement>(null)
  const currentUserId = getUserId()

  const { data: usersData, isLoading: isUsersLoading } =
    useGetAllUsersOfAccountQuery(graphqlRequestClient)

  const { data: fundsData, isLoading: isFundsLoading } = useGetAllFundsOfUserQuery(
    graphqlRequestClient,
    { input: selectedUserId },
    { enabled: !!selectedUserId }
  )

  const { data: companiesData, isLoading: isCompaniesLoading } =
    useGetExistingCompaniesOfInvestorQuery<GetExistingCompaniesOfInvestorQuery, Error>(
      graphqlRequestClient,
      { input: { userId: selectedUserId, investorId: selectedFundId } },
      { enabled: !!selectedFundId && !!selectedUserId }
    )

  const removeUserMutation = useRemoveUserFromAccountMutation(graphqlRequestClient, {
    onSuccess: () => {
      queryClient.invalidateQueries(useGetAllUsersOfAccountQuery.getKey())
      Toast('User deleted successfully', ToastType.success)
      setSelectedUserId(undefined)
      setSelectedUserName(undefined)
      setSelectedUserRole(null)
      setSelectedFundId(null)
      setSelectedUserIsAccountAdmin(false)
    },
    onError: (error: any) => {
      const message =
        error?.response?.errors?.[0]?.message || error.message || 'Failed to delete user'
      Toast(message, ToastType.error)
    },
  })

  const { userOptions, firstUser, firstFund } = useMemo(() => {
    const rawUsers = (usersData?.getAllUsersOfAccount ?? []).filter(
      (user): user is NonNullable<typeof user> => user !== null
    )

    const options: UserOption[] = rawUsers.map((user) => ({
      label: user.name ?? '',
      value: String(user.id),
      role: user.role ?? '',
      name: user.name ?? '',
      email: user.emailId ?? '',
      accountId: user.accountId?.toString(),
      isAccountAdmin: user.isAccountAdmin ?? false,
    }))

    const first = rawUsers[0]
    const firstFundId = fundsData?.getAllFundsOfUser?.[0]?.id ?? null

    return { userOptions: options, firstUser: first, firstFund: firstFundId }
  }, [usersData, fundsData])

  useEffect(() => {
    if (!selectedUserId && firstUser && !isUsersLoading) {
      setSelectedUserId(String(firstUser.id))
      setSelectedUserName(firstUser.name ?? '')
      setSelectedUserRole(firstUser.role ?? null)
      setSelectedUserAccountId(firstUser.accountId?.toString() ?? null)
      setSelectedUserIsAccountAdmin(firstUser.isAccountAdmin ?? false)
    }
  }, [firstUser, selectedUserId, isUsersLoading])

  useEffect(() => {
    if (selectedUserId && firstFund && selectedFundId === null && !isFundsLoading) {
      setSelectedFundId(firstFund)
    }
  }, [selectedUserId, firstFund, selectedFundId, isFundsLoading])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node

      if (isAddDropdownOpen && addDropdownRef.current && !addDropdownRef.current.contains(target)) {
        setIsAddDropdownOpen(false)
      }

      if (
        isUserDropdownOpen &&
        userDropdownRef.current &&
        !userDropdownRef.current.contains(target)
      ) {
        setIsUserDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isAddDropdownOpen, isUserDropdownOpen])

  const fundOptions =
    fundsData?.getAllFundsOfUser?.map((fund: any) => ({
      label: fund.name,
      value: fund.id,
    })) || []

  const companies: SyncCompanyInput[] = useMemo(
    () =>
      companiesData?.getCompaniesOfOtherInvestor
        ?.filter((c): c is NonNullable<typeof c> => !!c?.companyId && !!c?.companyName)
        .map((c) => ({
          companyId: c.companyId!,
          companyName: c.companyName!,
          companyType: c.companyType ?? 'Manual',
        })) || [],
    [companiesData]
  )

  const handleFundSelect = (fundId: number) => setSelectedFundId(fundId)

  const confirmDeleteUser = () => {
    if (!selectedUserId) return
    setIsDeleteLoading(true)
    removeUserMutation.mutate(
      { input: Number(selectedUserId) },
      {
        onSettled: () => {
          setIsDeleteLoading(false)
          setShowConfirmDelete(false)
        },
      }
    )
  }

  const handleUpdateRoleClose = () => {
    setShowUpdateRole(false)
    queryClient.invalidateQueries(useGetAllUsersOfAccountQuery.getKey())
    if (selectedUserId) {
      queryClient.invalidateQueries(useGetAllFundsOfUserQuery.getKey({ input: selectedUserId }))
    }
  }

  const addActions: UserAction[] = [
    {
      id: 'add-fund',
      label: 'Add Fund',
      icon: '/media/icons/duotune/arrows/arr075.svg',
      onClick: () => {
        setShowAddFund(true)
        queryClient.invalidateQueries(useGetAllFundsOfUserQuery.getKey())
        queryClient.invalidateQueries(useGetAllUsersOfAccountQuery.getKey())
        queryClient.invalidateQueries(useGetExistingCompaniesOfInvestorQuery.getKey())
        setIsAddDropdownOpen(false)
      },
      visible: true,
    },
    {
      id: 'sync-investor',
      label: 'Sync Fund Companies',
      icon: '/media/icons/duotune/general/sync-investor.svg',
      onClick: () => {
        setShowSyncInvestor(true)
        setIsAddDropdownOpen(false)
      },
      visible: true,
    },
  ]

  const userActions: UserAction[] = useMemo(() => {
    const isAdmin = selectedUserRole?.toLowerCase() === 'admin'
    const isCurrentUser = String(selectedUserId) === String(currentUserId)
    return [
      {
        id: 'add-user',
        label: 'Add User',
        icon: '/media/icons/duotune/communication/com013.svg',
        onClick: () => {
          setShowAddUser(true)
          setIsUserDropdownOpen(false)
          queryClient.invalidateQueries(useGetAllUsersOfAccountQuery.getKey())
          queryClient.invalidateQueries(useGetAllFundsOfUserQuery.getKey())
        },
        visible: isAdmin,
      },
      {
        id: 'update-role',
        label: 'Update Role',
        icon: '/media/icons/duotune/general/gen055.svg',
        onClick: () => {
          const selected = userOptions.find((u) => u.value === selectedUserId)
          setSelectedUserName(selected?.name)
          setSelectedUserRole(selected?.role || null)
          setShowUpdateRole(true)
          setIsUserDropdownOpen(false)
        },
        visible: !isCurrentUser,
      },
      {
        id: 'delete-user',
        label: isDeleteLoading ? 'Deleting...' : 'Delete User',
        icon: '/media/icons/duotune/general/gen027.svg',
        onClick: () => {
          setShowConfirmDelete(true)
          setIsUserDropdownOpen(false)
        },
        visible: !isCurrentUser,
      },
    ]
  }, [selectedUserRole, selectedUserId, userOptions, isDeleteLoading, currentUserId])

  const toggleAddDropdown = () => {
    setIsAddDropdownOpen((prev) => !prev)
    if (isUserDropdownOpen) setIsUserDropdownOpen(false)
  }

  const toggleUserDropdown = () => {
    setIsUserDropdownOpen((prev) => !prev)
    if (isAddDropdownOpen) setIsAddDropdownOpen(false)
  }

  const renderDropdown = (
    actions: UserAction[],
    isOpen: boolean,
    toggle: () => void,
    ref: React.RefObject<HTMLDivElement>
  ) => (
    <div className='position-relative' ref={ref}>
      <button
        type='button'
        className='btn btn-primary dropdown-toggle px-3 px-md-4 py-2 fw-semibold w-100'
        onClick={toggle}
        style={{ minWidth: 'fit-content', height: '38px' }}
      >
        {actions[0]?.id.includes('add-fund') ? 'Fund Actions' : 'User Actions'}
      </button>
      {isOpen && (
        <div
          className='position-absolute mt-2 bg-white border border-light-subtle rounded-3 shadow-sm border border-1 border-gray-400'
          style={{ top: '100%', right: 0, left: 'auto', minWidth: '210px', zIndex: 1000 }}
        >
          {actions
            .filter((a) => a.visible)
            .map((action, index, arr) => (
              <div
                key={action.id}
                className='px-4 py-3 cursor-pointer bg-hover-light-subtle text-hover-primary d-flex align-items-center '
                onClick={() => {
                  action.onClick()
                  if (actions[0].id.includes('add-fund')) {
                    setIsAddDropdownOpen(false)
                  } else {
                    setIsUserDropdownOpen(false)
                  }
                }}
              >
                <KTSVG
                  path={action.icon}
                  className='svg-icon-1 text-gray-700 text-hover-primary me-2'
                />
                <span className='fw-semibold fs-7'>
                  {action.id === 'delete-user' && isDeleteLoading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2' role='status' />
                      {action.label}
                    </>
                  ) : (
                    action.label
                  )}
                </span>
              </div>
            ))}
        </div>
      )}
    </div>
  )

  return (
    <div className='container-fluid px-1 px-md-2 px-lg-1 py-3 py-md-4 py-lg-0'>
      <div className='card border border-light-subtle shadow-sm mb-3 mb-md-4 mb-lg-6'>
        <div className='card-body p-3 p-md-4 p-lg-6'>
          <div className='d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3 mb-md-4 gap-2'>
            <h2 className='card-title fw-bold mb-0 fs-4 fs-md-3'>Manage Users</h2>
            {superAdmin && (
              <button
                type='button'
                className='btn btn-sm btn-primary d-flex align-items-center'
                onClick={() => setShowAddAccount(true)}
              >
                <Icon icon='mdi:bank-plus' className='me-2' width={18} height={18} />
                Add Account
              </button>
            )}
          </div>

          {/* User Select + Two Dropdowns */}
          <div className='d-flex flex-column flex-lg-row align-items-stretch align-items-lg-end gap-3 mt-3'>
            {/* User Select */}
            <div
              className='flex-shrink-1 w-100 w-md-75 w-lg-50'
              style={{ minWidth: '280px', maxWidth: '600px' }}
            >
              <Select<UserOption>
                options={userOptions}
                styles={selectCustomStyles as any}
                name='userType'
                onChange={(newValue) => {
                  if (newValue) {
                    setSelectedUserId(newValue.value)
                    setSelectedUserName(newValue.name)
                    setSelectedUserRole(newValue.role || null)
                    setSelectedUserAccountId(newValue.accountId || null)
                    setSelectedUserIsAccountAdmin(newValue.isAccountAdmin || false)
                    setSelectedFundId(null)
                    setIsAddDropdownOpen(false)
                    setIsUserDropdownOpen(false)
                  }
                }}
                placeholder='Select a user...'
                value={userOptions.find((option) => option.value === selectedUserId) || null}
                getOptionLabel={(option) => option.name}
                formatOptionLabel={(option, { context }) => {
                  const roleLabel = option.role || ''
                  const isHovered = hoveredOption === option.value
                  let badgeClass = ''
                  const badgeStyle: React.CSSProperties = {
                    fontSize: '10px',
                    transition: 'all 0.2s',
                  }

                  if (isHovered) {
                    badgeClass =
                      roleLabel.toLowerCase() === 'admin'
                        ? 'border border-white text-white bg-success'
                        : roleLabel.toLowerCase() === 'user'
                        ? 'border border-white text-white bg-primary'
                        : 'border border-white text-white bg-secondary'
                  } else {
                    badgeClass =
                      roleLabel.toLowerCase() === 'admin'
                        ? 'border border-success text-success'
                        : roleLabel.toLowerCase() === 'user'
                        ? 'border border-primary text-primary'
                        : 'border border-secondary text-secondary'
                  }

                  if (context === 'value') {
                    return (
                      <div className='d-flex align-items-center gap-2 flex-wrap'>
                        <span className='text-truncate'>{option.name}</span>
                        {roleLabel && (
                          <span
                            className={`px-2 rounded ${badgeClass}`}
                            style={{ ...badgeStyle, fontWeight: '700' }}
                          >
                            {roleLabel.toUpperCase()}
                          </span>
                        )}
                      </div>
                    )
                  }

                  return (
                    <div
                      className='d-flex flex-column'
                      onMouseEnter={() => setHoveredOption(option.value)}
                      onMouseLeave={() => setHoveredOption(null)}
                    >
                      <div className='d-flex align-items-center gap-2 flex-wrap'>
                        <span className='fw-semibold'>{option.name}</span>
                        {roleLabel && (
                          <span
                            className={`px-2 rounded ${badgeClass}`}
                            style={{ ...badgeStyle, fontWeight: '600' }}
                          >
                            {roleLabel.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <span className='text-500 fs-8 text-truncate'>{option.email}</span>
                    </div>
                  )
                }}
              />
            </div>

            {/* Two Dropdowns */}
            {selectedUserId && (
              <div className='d-flex flex-column flex-sm-row gap-2 gap-md-3 ms-lg-auto mt-3 mt-lg-0 w-100 w-lg-auto'>
                {renderDropdown(addActions, isAddDropdownOpen, toggleAddDropdown, addDropdownRef)}

                {/* Show simple Add User button if current user is viewing themselves, otherwise show dropdown */}
                {String(selectedUserId) === String(currentUserId) &&
                selectedUserRole?.toLowerCase() === 'admin' ? (
                  <button
                    type='button'
                    className='btn btn-primary px-3 px-md-4 py-2 fw-semibold w-100'
                    onClick={() => setShowAddUser(true)}
                    style={{ minWidth: 'fit-content', height: '38px' }}
                  >
                    Add User
                  </button>
                ) : (
                  renderDropdown(
                    userActions,
                    isUserDropdownOpen,
                    toggleUserDropdown,
                    userDropdownRef
                  )
                )}
              </div>
            )}
          </div>
        </div>

        {/* Tabs + Content */}
        {(fundsData?.getAllFundsOfUser?.length ?? 0) > 0 && selectedUserId ? (
          <>
            <div className='card-header bg-white px-3 px-md-4 px-lg-6 pt-6 pb-0 mb-0'>
              <ul
                className='nav nav-tabs border-0 fs-4 fw-bold mb-0'
                role='tablist'
                style={{ borderBottom: '1px solid #e4e6ef' }}
              >
                <li className='nav-item' role='presentation'>
                  <button
                    className={`nav-link border-0 px-3 py-2 position-relative ${
                      activeTab === 'funds' ? 'text-dark fw-bold' : 'text-muted'
                    }`.trim()}
                    onClick={() => setActiveTab('funds')}
                    type='button'
                    role='tab'
                    style={{ marginBottom: '-2px' }}
                  >
                    Funds
                    {activeTab === 'funds' && (
                      <span
                        className='position-absolute start-0 end-0 bottom-0 border-bottom border-3 border-primary'
                        style={{ transform: 'translateY(0px)' }}
                      />
                    )}
                  </button>
                </li>
                <li className='nav-item' role='presentation'>
                  <button
                    className={`nav-link border-0 px-3 py-2 position-relative ${
                      activeTab === 'companies' ? 'text-dark fw-bold' : 'text-muted'
                    }`.trim()}
                    onClick={() => setActiveTab('companies')}
                    type='button'
                    role='tab'
                    style={{ marginBottom: '-2px' }}
                  >
                    Companies
                    {activeTab === 'companies' && (
                      <span
                        className='position-absolute start-0 end-0 bottom-0 border-bottom border-3 border-primary'
                        style={{ transform: 'translateY(0px)' }}
                      />
                    )}
                  </button>
                </li>
              </ul>
            </div>

            <div className='card-body p-0'>
              {activeTab === 'funds' && (
                <div style={{ maxHeight: '60vh', overflowY: 'auto', overflowX: 'auto' }}>
                  <FundTable
                    selectedFundId={selectedFundId!}
                    selectedUserId={selectedUserId}
                    onFundSelect={handleFundSelect}
                  />
                </div>
              )}

              {activeTab === 'companies' && (
                <div className='p-3 p-md-4 p-lg-6'>
                  <div className='row g-3 g-md-4'>
                    <div className='col-12 col-lg-6'>
                      <h4 className='required fw-semibold mb-2 mb-md-3 fs-6'>Select Fund:</h4>
                      <Select
                        options={fundOptions}
                        styles={selectCustomStyles}
                        name='fundType'
                        onChange={(e: any) => e?.value && handleFundSelect(e.value)}
                        placeholder='Select a fund...'
                        value={
                          fundOptions.find((option: any) => option.value === selectedFundId) || null
                        }
                        isDisabled={fundOptions.length === 0}
                      />
                      {fundOptions.length === 0 && (
                        <div className='text-muted mt-2 fs-7 fs-md-6'>
                          No funds available for the selected user
                        </div>
                      )}
                    </div>
                  </div>
                  {selectedFundId && (
                    <>
                      {companiesData?.getCompaniesOfOtherInvestor &&
                      companiesData.getCompaniesOfOtherInvestor.length > 0 ? (
                        <div className='mt-3 mt-md-4 mt-lg-6'>
                          <div style={{ maxHeight: '400px', overflowY: 'auto', overflowX: 'auto' }}>
                            <RemoveCompanies
                              companies={companies}
                              selectedUserId={selectedUserId}
                              selectedFundId={selectedFundId}
                              isLoading={isCompaniesLoading || isFundsLoading}
                              accountId={accountId}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className='d-flex flex-column justify-content-center align-items-center text-center py-6 py-md-8 py-lg-10'>
                          <div className='bg-light-subtle rounded-3 p-4 p-md-6 p-lg-8 border border-light-subtle'>
                            <img
                              alt='no companies'
                              src={toAbsoluteUrl('/media/custom-images/emptyRecentCompanies.svg')}
                              className='h-100px h-md-125px h-lg-150px mb-3 mb-md-4'
                            />
                            <h4 className='text-muted mb-2 fs-6 fs-md-5'>No companies found</h4>
                            <div className='fw-normal fs-7 fs-md-6 text-muted'>
                              The selected fund has no associated companies to manage.
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className='card-body p-3 p-md-4 p-lg-6' style={{ minHeight: '300px' }}>
            {!isUsersLoading ? (
              <div className='d-flex flex-column justify-content-center align-items-center h-100 text-center py-4 py-md-6 py-lg-8'>
                <div className='bg-light-subtle rounded-3 p-4 p-md-6 p-lg-8 border border-light-subtle'>
                  <img
                    alt='no items'
                    src={toAbsoluteUrl('/media/custom-images/emptyRecentCompanies.svg')}
                    className='h-100px h-md-125px h-lg-150px mb-3 mb-md-4'
                  />
                  <h4 className='text-muted mb-2 fs-6 fs-md-5'>No User selected</h4>
                  <div className='fw-normal fs-7 fs-md-6 text-muted'>
                    Please select a user above to view their funds and manage companies
                  </div>
                </div>
              </div>
            ) : (
              <div className='d-flex justify-content-center align-items-center h-100'>
                <span className='spinner-grow' />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Popups */}
      <UpdateUserRolePopup
        show={showUpdateRole}
        onHide={handleUpdateRoleClose}
        userId={selectedUserId!}
        userName={selectedUserName!}
        currentRole={selectedUserRole}
      />

      <DeleteConfirmPopup
        showModel={showConfirmDelete}
        setShowModel={setShowConfirmDelete}
        loading={isDeleteLoading}
        title='Delete User'
        description={`Are you sure you want to permanently delete "${selectedUserName}"?`}
        notes='If you delete this user, they will be removed from all associated funds and companies and cannot be undone.'
        confirmText={`Type the user name "${selectedUserName}" to confirm`}
        currentValue={selectedUserName || ''}
        primaryButtonText='Delete'
        secondaryButtonText='Cancel'
        primaryAction={confirmDeleteUser}
      />

      <AddFundPopup
        show={showAddFund}
        onHide={() => setShowAddFund(false)}
        selectedUserId={selectedUserId!}
        selectedUserName={selectedUserName!}
        selectedUserAccountId={selectedUserAccountId!}
        selectedUserRole={selectedUserRole}
        selectedUserIsAccountAdmin={selectedUserIsAccountAdmin}
        allUsers={userOptions}
      />
      {showSyncInvestor && (
        <SyncInvestorPopup
          show={showSyncInvestor}
          handleClose={() => setShowSyncInvestor(false)}
          selectedUserId={selectedUserId}
        />
      )}

      <AddUserPopup
        show={showAddUser}
        onHide={() => setShowAddUser(false)}
        accountId={accountId}
        allUsers={userOptions}
      />
      {showAddAccount && (
        <AddAccountPopup show={showAddAccount} handleClose={() => setShowAddAccount(false)} />
      )}
    </div>
  )
}

export default RemoveUsers
